import { styled } from 'styled-components';
import { getObjectWithSelector } from '../../../functions/objectFunctions/getObjectWithSelector';
import { updateObjectNestedProperties } from '../../../functions/objectFunctions/updateObjectNestedProperties';
import { camelCaseToKebabCase } from '../../../functions/utils/camelCaseToKebabCase';
import { useElements, useScene } from '../../../hooks';

export const VisibilityToggle = ({
  forState,
  defaultForState,
}: {
  forState?: string;
  defaultForState?: string;
}) => {
  const scene = useScene();
  const lastSelectedElementID = scene.selectedElementIDs.slice(-1)[0];
  const [lastSelectedElement, setLastSelectedElement] = useElements({
    id: lastSelectedElementID,
  });

  const styleObj = getObjectWithSelector(lastSelectedElement, `props.class`);
  let defaultIsVisible = styleObj?.display === 'none' ? false : true;

  if (defaultForState) {
    const defaultForStateValue = getObjectWithSelector(
      lastSelectedElement,
      `props.class.variants.${camelCaseToKebabCase(defaultForState)}.true`
    )?.display;

    if (defaultForStateValue !== undefined)
      defaultIsVisible = defaultForStateValue === 'none' ? false : true;
  }

  let variantStyleObj;
  let isVisible = true;
  let isUndefined = true;

  if (forState) {
    variantStyleObj = getObjectWithSelector(
      lastSelectedElement,
      `props.class.variants.${camelCaseToKebabCase(forState)}.true`
    );
    isVisible =
      variantStyleObj?.display === 'none'
        ? false
        : variantStyleObj?.display === undefined
        ? defaultIsVisible
        : true;
    isUndefined = variantStyleObj?.display === undefined;
  } else {
    variantStyleObj = styleObj;
    isVisible = defaultIsVisible;
    isUndefined = styleObj?.display === undefined;
  }

  const toggleVisibility = () => {
    let newElement;
    if (forState) {
      newElement = updateObjectNestedProperties(lastSelectedElement, {
        [`props.class.variants.${camelCaseToKebabCase(forState)}.true.display`]:
          isVisible ? 'none' : 'flex',
      });
    } else {
      newElement = updateObjectNestedProperties(lastSelectedElement, {
        'props.class.display': isVisible ? 'none' : 'flex',
      });
    }
    setLastSelectedElement(newElement);
  };

  const setAsUndefined = () => {
    let newElement;
    if (forState) {
      newElement = updateObjectNestedProperties(lastSelectedElement, {
        [`props.class.variants.${camelCaseToKebabCase(forState)}.true.display`]:
          undefined,
      });
    } else {
      newElement = updateObjectNestedProperties(lastSelectedElement, {
        'props.class.display': undefined,
      });
    }
    setLastSelectedElement(newElement);
  };

  const displayIcon = () => {
    switch (isVisible) {
      case true:
        return <i className="active icon-eye" />;
      case false:
        return <i className="icon-eye-off" />;
    }
  };

  return (
    <VisibilityToggleContainer
      $isUndefined={isUndefined}
      onClick={toggleVisibility}
      onDoubleClick={setAsUndefined}
    >
      {displayIcon()}
    </VisibilityToggleContainer>
  );
};

const VisibilityToggleContainer = styled.div<{ $isUndefined: boolean }>`
  position: relative;
  cursor: pointer;
  width: 100%;
  margin: 2px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  color: var(--text-color-secondary);
  border-radius: 10px;

  & > i {
    font-size: 12px;
    transition: color 0.3s ease;
    &.active {
      color: var(--accent-color);
    }
  }

  & > span {
    font-size: 9px;
  }

  transition: background-color 0.3s ease;
  &:hover {
    background-color: var(--panel-bg-color-hover);
  }

  ${({ $isUndefined }) =>
    !$isUndefined &&
    `
      &:after {
        content: '';
        position: absolute;
        bottom: -3px;
        width: 50%;
        height: 1px;

        background: linear-gradient(
    90deg,
    transparent,
    var(--text-color-secondary) 10%,
    var(--text-color-secondary) 90%,
    transparent
  );
      }
    `}
`;
